import {
  APICallState,
  LoginInfo,
  Selectors,
  useIsDesktop,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useDispatch, useSelector} from 'Provider';
import React, {useCallback} from 'react';

import BusinessInfoModule from 'components/BusinessInfoModule';
import GatedBusinessInfoModule from 'components/GatedProfilePanels/GatedBusinessInfoModule';
import MFAGate from 'containers/MFAGate';
import {useMFAStepUp} from 'hooks/useMFAStepUp';
import {useMaskPhoneNumber} from 'hooks/useMaskPhoneNumber';
import {useMaskZip} from 'hooks/useMaskZip';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {profileTabForwardUrl} from 'models/MFA';
import {ProfileActions, profileInfoStateSelector} from 'store/profile';

const ConnectedBusinessInfoModule: React.FC = () => {
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const {maskNumber} = useMaskPhoneNumber();
  const {onEdit, stopEditing, isPanelUnlocked, isMFAStepUpLoading, isEditing} =
    useMFAStepUp({
      forwardUrl: profileTabForwardUrl,
    });
  const profileInfoState = useSelector(profileInfoStateSelector);
  const logininfo = useWidgetState(Selectors.loginInfoSelector) as LoginInfo;
  const getGuestProfileCallState = useWidgetState(
    Selectors.getGuestProfileCallStateSelector
  ) as APICallState;
  const {trackAdobeEvent} = useTrackEvent();

  const businessAddress = logininfo.addresses?.address.find(
    a => a.location === 'Business'
  );
  const businessPhone = logininfo.phonenumbers?.find(
    n => n.location === 'Business'
  )?.phonenumber;

  const country = businessAddress?.country?.toUpperCase();
  const maskZip = useMaskZip({
    isCanada: country === 'CAN',
    isUSA: country === 'USA',
  });
  const zip =
    businessAddress?.zipcode === '000000'
      ? undefined
      : maskZip(businessAddress?.zipcode);

  const handleSaveProfileInfo = useCallback(
    data => {
      dispatch(ProfileActions.updateProfileInfo(data));
    },
    [dispatch]
  );

  const onOverlayAnimationFinish = () => {
    if (profileInfoState === APICallState.SUCCESS) {
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {
          name: 'business information edited',
        },
      });
      stopEditing();
    }
    dispatch(ProfileActions.resetProfileInfoState());
  };

  return (
    <MFAGate
      forwardUrl={profileTabForwardUrl}
      GatedComponent={GatedBusinessInfoModule}
      forceLoading={getGuestProfileCallState !== APICallState.SUCCESS}>
      <BusinessInfoModule
        isEditLoading={isMFAStepUpLoading && isEditing}
        handleSaveProfileInfo={handleSaveProfileInfo}
        address={businessAddress?.address1}
        address2={businessAddress?.address2}
        phonenumber={maskNumber(businessPhone)}
        city={businessAddress?.city}
        country={country}
        stateprovince={businessAddress?.stateprovince}
        zip={zip}
        profileInfoState={profileInfoState}
        onEdit={onEdit}
        stopEditing={stopEditing}
        onOverlayAnimationFinish={onOverlayAnimationFinish}
        isPanelUnlocked={isPanelUnlocked && isEditing}
      />
    </MFAGate>
  );
};

export default ConnectedBusinessInfoModule;
